import { Auth } from 'aws-amplify';
import { notificationManager } from './NotificationUtils';

/**
 * Check if user has MFA enabled
 * @param {Object} user - Cognito user object
 * @returns {boolean} - True if MFA is enabled
 */
export const isMFAEnabled = (user) => {
  try {
    if (!user || !user.preferredMFA) {
      return false;
    }
    return user.preferredMFA === 'SOFTWARE_TOKEN_MFA';
  } catch (error) {
    console.error('Error checking MFA status:', error);
    return false;
  }
};

/**
 * Check if user has a CMS role that requires MFA
 * @param {Object} userAttributes - User attributes from Cognito
 * @returns {boolean} - True if user is logged in (all users can use MFA)
 */
export const isCMSUser = (userAttributes) => {
  try {
    const userRole = userAttributes?.['custom:role'] || userAttributes?.role;
    // Return true if user has any role (all users can use MFA)
    return !!userRole;
  } catch (error) {
    console.error('Error checking user role:', error);
    return false;
  }
};

// /**
//  * Check if user has a CMS role that requires MFA
//  * Excludes StaffConnect roles (Chat Agent, Chat Manager, DET User, Analytics Manager, Front Desk Staff)
//  * @param {Object} userAttributes - User attributes from Cognito
//  * @returns {boolean} - True if user has CMS role (not StaffConnect role)
//  */
// export const isCMSUser = (userAttributes) => {
//   // StaffConnect roles that should NOT have MFA (they use different product)
//   const staffConnectRoles = [
//     'Chat Agent',
//     'Chat Manager',
//     'DET User',
//     'Analytics Manager',
//     'Front Desk Staff',
//   ];

//   try {
//     const userRole = userAttributes?.['custom:role'] || userAttributes?.role;
//     if (!userRole) return false;

//     // Check if user role is a StaffConnect role - if yes, they should NOT use CMS MFA
//     const isStaffConnectUser = staffConnectRoles.some((role) =>
//       userRole.toLowerCase().includes(role.toLowerCase())
//     );

//     // Return true only if user is NOT a StaffConnect user (meaning they are a CMS user)
//     return !isStaffConnectUser;
//   } catch (error) {
//     console.error('Error checking user role:', error);
//     return false;
//   }
// };
/**
 * Setup MFA - Associate software token and get secret code
 * @returns {Promise<string>} - Secret code for QR generation
 */
export const setupMFA = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const secretCode = await Auth.setupTOTP(user);
    return secretCode;
  } catch (error) {
    console.error('Error setting up MFA:', error);
    notificationManager('danger', 'Failed to setup MFA: ' + error.message);
    throw error;
  }
};

/**
 * Verify MFA setup with TOTP code
 * @param {string} totpCode - 6-digit code from authenticator app
 * @returns {Promise<boolean>} - True if verification successful
 */
export const verifyMFASetup = async (totpCode) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.verifyTotpToken(user, totpCode);
    
    // Enable MFA for the user
    await Auth.setPreferredMFA(user, 'TOTP');
    
    notificationManager('success', 'MFA enabled successfully');
    return true;
  } catch (error) {
    console.error('Error verifying MFA:', error);
    notificationManager('danger', 'Invalid verification code');
    return false;
  }
};

/**
 * Confirm sign-in with MFA code
 * @param {Object} user - Cognito user object from sign-in
 * @param {string} mfaCode - 6-digit MFA code
 * @returns {Promise<Object>} - Authenticated user object
 */
export const confirmSignInWithMFA = async (user, mfaCode) => {
  try {
    const loggedUser = await Auth.confirmSignIn(user, mfaCode, 'SOFTWARE_TOKEN_MFA');
    return loggedUser;
  } catch (error) {
    console.error('Error confirming MFA:', error);
    throw error;
  }
};

/**
 * Disable MFA for current user
 * @returns {Promise<boolean>} - True if disabled successfully
 */
export const disableMFA = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.setPreferredMFA(user, 'NOMFA');
    notificationManager('success', 'MFA disabled successfully');
    return true;
  } catch (error) {
    console.error('Error disabling MFA:', error);
    notificationManager('danger', 'Failed to disable MFA: ' + error.message);
    return false;
  }
};

/**
 * Get MFA status for current user
 * @returns {Promise<Object>} - MFA status object
 */
export const getMFAStatus = async () => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    const preferredMFA = await Auth.getPreferredMFA(user);
    
    return {
      enabled: preferredMFA === 'SOFTWARE_TOKEN_MFA',
      preferredMFA: preferredMFA,
    };
  } catch (error) {
    console.error('Error getting MFA status:', error);
    return {
      enabled: false,
      preferredMFA: 'NOMFA',
    };
  }
};

/**
 * Generate QR code URL for authenticator apps
 * @param {string} secretCode - Secret code from setupMFA
 * @param {string} userEmail - User's email address
 * @returns {string} - OTP Auth URL for QR code generation
 */
export const generateQRCodeURL = (secretCode, userEmail) => {
  const appName = 'Hudini-CMS';
  const issuer = 'Hudini';
  return `otpauth://totp/${appName}:${userEmail}?secret=${secretCode}&issuer=${issuer}`;
};
